import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { ButtonBase, Chip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum, LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { HIDDEN_IN_PRINT } from "../print/printPageOnly"

import { chatIconConfig } from "./OrderChatIcon"
import { ChatIconVariant, ChatMessageAuthorRole, UserType, getChatIconVariant } from "./orderChatTypes"
import type { ChatMessage } from "./orderChatTypes"
import { useOrderChat } from "./useOrderChat"

const useStyles = makeStyles()((theme) => ({
    card: {
        ...HIDDEN_IN_PRINT,
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        width: "100%",
        padding: ".5rem 1rem",
        textAlign: "left",
        borderRadius: ".625rem",
        background: theme.palette.tailwind.amber[50],
        border: `1px solid ${theme.palette.tailwind.amber[200]}`,
    },
    badge: {
        flexShrink: 0,
        fontWeight: 700,
        color: theme.palette.common.white,
        background: theme.palette.tailwind.amber[700],
    },
    icon: {
        flexShrink: 0,
        fontSize: "1.125rem",
    },
    iconNeedsResponse: {
        color: theme.palette.tailwind.blue[400],
    },
    iconResponded: {
        color: theme.palette.tailwind.slate[400],
    },
    preview: {
        flex: 1,
        minWidth: 0,
    },
    secondary: {
        color: theme.palette.tailwind.slate[600],
    },
}))

type OrderChatStatusCardProps = Readonly<{
    orderId: number
    viewerRole?: ChatMessageAuthorRole
    hasChat?: boolean
    orderMessages?: ChatMessage[]
    onClick: () => void
    className?: string
}>

export function OrderChatStatusCard({
    orderId,
    viewerRole,
    hasChat = true,
    orderMessages,
    onClick,
    className,
}: OrderChatStatusCardProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { messages } = useOrderChat(hasChat ? orderId : 0)
    const latestMessage = messages[messages.length - 1]

    if (!latestMessage) return null

    const viewerUserType =
        viewerRole && (viewerRole === ChatMessageAuthorRole.MERCHANT ? UserType.MERCHANT : UserType.USER)
    const iconVariant = getChatIconVariant(orderMessages) ?? ChatIconVariant.RESPONDED
    const iconVariantClasses: Record<ChatIconVariant, string> = {
        [ChatIconVariant.NEEDS_RESPONSE]: classes.iconNeedsResponse,
        [ChatIconVariant.RESPONDED]: classes.iconResponded,
    }

    const sentAt = dateTimeFactory.fromApi(latestMessage.created)
    const replyVars = {
        date: dateTimeFactory.formatMonthDay(sentAt),
        time: dateTimeFactory.formatTime(sentAt),
        message: latestMessage.body,
    }
    const replyElems = { quote: <span className={classes.secondary} /> }
    const replyText = (
        <Typography component="span" variant="body2" noWrap className={classes.preview}>
            {latestMessage.creator.displayName} replied on {replyVars.date} at {replyVars.time}: “{latestMessage.body}”
        </Typography>
    )

    return (
        <ButtonBase className={cx(classes.card, className)} onClick={onClick} focusRipple>
            <FontAwesomeIcon
                icon={chatIconConfig[iconVariant].icon}
                className={cx(classes.icon, iconVariantClasses[iconVariant])}
            />
            {latestMessage.creator.userType === viewerUserType ? (
                <LocalizedStrict id="order-chat-status-card-reply-own" vars={replyVars} elems={replyElems}>
                    {replyText}
                </LocalizedStrict>
            ) : (
                <LocalizedEnum
                    base="order-chat-status-card-reply"
                    enumValue={latestMessage.creator.userType}
                    vars={replyVars}
                    elems={replyElems}
                >
                    {replyText}
                </LocalizedEnum>
            )}
            <LocalizedStrict
                id="order-chat-status-card-messages-badge"
                vars={{ count: messages.length }}
                attrs={{ label: true }}
            >
                <Chip component="span" size="small" label={`${messages.length} messages`} className={classes.badge} />
            </LocalizedStrict>
            <FontAwesomeIcon icon={faChevronRight} />
        </ButtonBase>
    )
}
